import { useCallback, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_SMS_AROUND } from '../../../lib/graphql/queries/contacts';
import type { GQLMessageItem } from './useContact';

interface AroundBucket {
  items: GQLMessageItem[];
  hasMore: boolean;
}

interface AroundData {
  contact: {
    contactId: string;
    older: AroundBucket;
    newer: AroundBucket;
  } | null;
}

const DEFAULT_LIMIT = 25;

/**
 * Anchored thread fetch — loads N messages strictly older than the
 * anchor plus N strictly newer, in a single GraphQL operation using
 * aliased fields (per Suleman's PR #2423 contract).
 *
 *   older: sms(before: anchor, sortOrder: "DESC")  ← closest-above first
 *   newer: sms(after:  anchor, sortOrder: "ASC")   ← closest-below first
 *
 * The anchor row itself is NOT included in either bunch — the caller
 * already has it (they clicked on the activity-feed row that triggered
 * the modal), so we render it locally between the two buckets.
 *
 * For top-down chat layout the FE reverses `older` client-side so the
 * unified render order is:
 *
 *   [older reversed → ASC]  [anchor]  [newer ASC]
 *   ↑ oldest at top                            ↑ newest at bottom
 *
 * `loadOlder` paginates further back: re-runs the around query with the
 * oldest-currently-rendered messageId as the new anchor, then prepends
 * just the older bucket from that response onto our accumulator. The
 * newer bucket of that response is ignored (it'd duplicate what's
 * already rendered).
 */
export function useContactSmsAroundGQL({
  contactId,
  anchorMessageId,
  enabled,
  limit = DEFAULT_LIMIT,
}: {
  contactId: string | null | undefined;
  anchorMessageId: string | null | undefined;
  enabled: boolean;
  limit?: number;
}) {
  const orgId = useOrganizationStore((s) => s.organization?.id) ?? '';
  const apolloClient = useApolloClient();

  const { data, loading, error } = useQuery<AroundData>(GET_CONTACT_SMS_AROUND, {
    variables: {
      organizationId: orgId,
      contactId: contactId ?? '',
      anchor: anchorMessageId ?? '',
      limit,
    },
    skip: !enabled || !orgId || !contactId || !anchorMessageId,
    fetchPolicy: 'cache-and-network',
  });

  // Extra older / newer messages loaded via paginated calls. Reset
  // when the initial anchor or contact changes — we're paging from a
  // new starting point.
  const [extraOlder, setExtraOlder] = useState<GQLMessageItem[]>([]);
  const [extraOlderHasMore, setExtraOlderHasMore] = useState<boolean | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [extraNewer, setExtraNewer] = useState<GQLMessageItem[]>([]);
  const [extraNewerHasMore, setExtraNewerHasMore] = useState<boolean | null>(null);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);

  // Reset accumulators on anchor / contact change. Comparison runs in
  // render — cheaper than a useEffect.
  const resetKey = `${contactId ?? ''}|${anchorMessageId ?? ''}`;
  const [trackedKey, setTrackedKey] = useState(resetKey);
  if (trackedKey !== resetKey) {
    setTrackedKey(resetKey);
    setExtraOlder([]);
    setExtraOlderHasMore(null);
    setExtraNewer([]);
    setExtraNewerHasMore(null);
  }

  const initialOlderRaw = data?.contact?.older?.items ?? [];
  // Backend returns `older` in DESC order (closest-above first) so we
  // can request just the N nearest above the anchor. The chat layout
  // reads top-down, so flip to ASC here. Extra (paginated) older was
  // already reversed before being stored, so just prepend it.
  const initialOlder = [...initialOlderRaw].reverse();
  const older = [...extraOlder, ...initialOlder];
  const initialNewer = data?.contact?.newer?.items ?? [];
  // Newer comes in ASC already (closest-below first). Paginated newer
  // appends to the end so the chronological order holds.
  const newer = [...initialNewer, ...extraNewer];

  const initialHasOlder = data?.contact?.older?.hasMore ?? false;
  const hasOlder = extraOlderHasMore ?? initialHasOlder;
  const initialHasNewer = data?.contact?.newer?.hasMore ?? false;
  const hasNewer = extraNewerHasMore ?? initialHasNewer;

  const loadOlder = useCallback(async () => {
    if (isLoadingOlder || !hasOlder) return;
    if (!orgId || !contactId) return;
    // Cursor = topmost rendered message.
    const cursor = older[0]?.messageId;
    if (!cursor) return;
    setIsLoadingOlder(true);
    try {
      const result = await apolloClient.query<AroundData>({
        query: GET_CONTACT_SMS_AROUND,
        variables: { organizationId: orgId, contactId, anchor: cursor, limit },
        fetchPolicy: 'network-only',
      });
      const olderItems = result.data?.contact?.older?.items ?? [];
      const olderAsc = [...olderItems].reverse();
      setExtraOlder((prev) => [...olderAsc, ...prev]);
      setExtraOlderHasMore(result.data?.contact?.older?.hasMore ?? false);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [isLoadingOlder, hasOlder, orgId, contactId, older, limit, apolloClient]);

  const loadNewer = useCallback(async () => {
    if (isLoadingNewer || !hasNewer) return;
    if (!orgId || !contactId) return;
    // Cursor = bottommost rendered message. If no newer messages have
    // come back yet, the anchor itself is the cursor.
    const cursor = newer[newer.length - 1]?.messageId ?? anchorMessageId ?? undefined;
    if (!cursor) return;
    setIsLoadingNewer(true);
    try {
      const result = await apolloClient.query<AroundData>({
        query: GET_CONTACT_SMS_AROUND,
        variables: { organizationId: orgId, contactId, anchor: cursor, limit },
        fetchPolicy: 'network-only',
      });
      const newerItems = result.data?.contact?.newer?.items ?? [];
      setExtraNewer((prev) => [...prev, ...newerItems]);
      setExtraNewerHasMore(result.data?.contact?.newer?.hasMore ?? false);
    } finally {
      setIsLoadingNewer(false);
    }
  }, [isLoadingNewer, hasNewer, orgId, contactId, newer, anchorMessageId, limit, apolloClient]);

  return {
    older,
    newer,
    hasOlder,
    hasNewer,
    loading,
    error,
    loadOlder,
    isLoadingOlder,
    loadNewer,
    isLoadingNewer,
  };
}
